import { ReactNode, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, ViewStyle, useWindowDimensions } from 'react-native';
import Animated, {
    Easing,
    SharedValue,
    cancelAnimation,
    runOnJS,
    runOnUI,
    useAnimatedStyle,
    useSharedValue,
    withDelay,
    withTiming,
} from 'react-native-reanimated';

import { ds } from '@/components/lib/design.system';
import { RaceToPlaceEntry } from '@/types/socialFeed.generated';
import isEqual from 'lodash/isEqual';

import { CardShadow } from './CardShadow';

/**
 * StackedCards
 *
 * This component renders a visual stack of three cards, but only the front card
 * ever displays real content. The middle and back cards are empty placeholders
 * used purely for the depth/stack illusion.
 *
 * Animation trigger:
 * - The entire animation cycle begins whenever the `data` prop changes.
 *   When new data arrives, the current front card is marked as exiting,
 *   animates off-screen to the right, and once the exit completes the card
 *   instances rotate forward so the next card becomes the new front card and
 *   receives the updated data.
 *
 * How the animation works:
 * - Three <Card> components are always mounted with positions: "back", "middle", "front".
 * - Only the "front" card receives `data` and renders content. The other cards
 *   always receive `null` and never show content.
 * - After the front card exits, we rotate React keys so:
 *     middle -> front, back -> middle, new card -> back.
 * - Because each Card animates based on its `position` prop, this key rotation
 *   makes the whole stack appear to shift forward.
 *
 * Content is anchored to the bottom of the card (contentWrapper: bottom: 0),
 * so height changes cause it to "grow upward" visually.
 *
 * Height is child-driven: StackedCards passes a SharedValue (cardHeight) to the
 * render function, and the child writes its measured height to it.
 *
 * During transitions, height is "locked" (lockedWrapperHeight) to prevent jumps.
 * Once the new card settles, the locked value animates to the new height and unlocks.
 */

export const STACK_CARDS_ANIMATION_CONFIG = { duration: 400, easing: Easing.linear };
const HEIGHT_ANIMATION_CONFIG = { duration: 300, easing: Easing.out(Easing.ease) };
const CONTENT_FADE_DELAY_MS = STACK_CARDS_ANIMATION_CONFIG.duration + HEIGHT_ANIMATION_CONFIG.duration;

const CARD_OFFSETS = { back: -24, middle: -12, front: 0 } as const;
const SCALE_PADDING = { middle: 16, back: 20 } as const;

interface CardProps {
    position: 'front' | 'middle' | 'back';
    data: RaceToPlaceEntry | null;
    isExiting: boolean;
    onExitComplete?: () => void;
    /** Called when card reaches front position */
    onForwardShiftComplete?: () => void;
    renderContent: (data: RaceToPlaceEntry, cardHeight: SharedValue<number>) => ReactNode;
    renderSkeleton?: () => ReactNode;
    showShadow?: boolean;
    cardHeight: SharedValue<number>;
    lockedWrapperHeight: SharedValue<number>;
    isHeightLocked: SharedValue<boolean>;
}

/** Individual card in the stack. Animates between back/middle/front positions. */
const Card = memo(
    ({
        position,
        data,
        isExiting,
        onExitComplete,
        onForwardShiftComplete,
        renderContent,
        renderSkeleton,
        showShadow = false,
        cardHeight,
        lockedWrapperHeight,
        isHeightLocked,
    }: CardProps) => {
        const { width } = useWindowDimensions();

        const frontCardWidth = width - 2 * 16;
        // 16px should be the space between first and middle card
        const middleCardWidth = frontCardWidth - 2 * SCALE_PADDING.middle;
        // 20px should be the space between middle and back card
        const backCardWidth = middleCardWidth - 2 * SCALE_PADDING.back;

        const cardPositions = useMemo(
            () => ({
                back: { scale: backCardWidth / frontCardWidth, translateY: CARD_OFFSETS.back },
                middle: { scale: middleCardWidth / frontCardWidth, translateY: CARD_OFFSETS.middle },
                front: { scale: 1.0, translateY: CARD_OFFSETS.front },
            }),
            [frontCardWidth, middleCardWidth, backCardWidth]
        );

        const initialConfig = cardPositions[position];

        // Horizontal offset (used for exit animation)
        const translateX = useSharedValue(0);
        // Vertical offset based on stack position (back/middle/front)
        const translateY = useSharedValue(initialConfig.translateY);
        // Scale based on stack position
        const scale = useSharedValue(initialConfig.scale);
        // Card container opacity (back card starts hidden and fades in)
        const opacity = useSharedValue(position === 'back' ? 0 : 1);
        // Opacity only for the inner content (only used on the front card)
        const contentOpacity = useSharedValue(position === 'front' && data ? 1 : 0);

        // For exiting cards: lock their height so they don't resize during exit animation
        // exitingContentHeight is a throwaway shared value passed to content so it doesn't affect cardHeight
        const lockedCardHeight = useSharedValue(0);
        const exiting = useSharedValue(0);
        const exitingContentHeight = useSharedValue(0);

        // Handle position changes and exit animations
        useEffect(() => {
            const config = cardPositions[position];
            const isFront = position === 'front';

            if (isExiting) {
                // Front card is exiting: slide it off-screen to the right.
                // First lock heights so the card doesn't resize during exit.
                const currentHeight = isHeightLocked.value ? lockedWrapperHeight.value : cardHeight.value;
                lockedCardHeight.value = currentHeight;
                exitingContentHeight.value = currentHeight;
                exiting.value = 1;

                // Cancel any in-progress animations and lock to front position.
                // Card should maintain full size/opacity as it exits.
                cancelAnimation(translateY);
                cancelAnimation(scale);
                cancelAnimation(opacity);
                cancelAnimation(contentOpacity);

                translateY.value = 0;
                scale.value = 1;
                opacity.value = 1;
                contentOpacity.value = 1;

                // Slide off to the right (width + 100 ensures fully off-screen)
                translateX.value = withTiming(width + 100, STACK_CARDS_ANIMATION_CONFIG, finished => {
                    if (finished && onExitComplete) {
                        runOnJS(onExitComplete)();
                    }
                });
            } else {
                // Regular position update for back/middle/front
                exiting.value = 0;

                translateX.value = withTiming(0, STACK_CARDS_ANIMATION_CONFIG);
                translateY.value = withTiming(config.translateY, STACK_CARDS_ANIMATION_CONFIG);
                opacity.value = withTiming(1, STACK_CARDS_ANIMATION_CONFIG);

                // When scale animation completes on front card, trigger the height unlock sequence
                scale.value = withTiming(config.scale, STACK_CARDS_ANIMATION_CONFIG, finished => {
                    if (finished && isFront && onForwardShiftComplete) {
                        runOnJS(onForwardShiftComplete)();
                    }
                });

                if (isFront) {
                    // Delay content fade-in until height resize is done
                    contentOpacity.value = withDelay(
                        CONTENT_FADE_DELAY_MS,
                        withTiming(1, { duration: 200, easing: Easing.linear })
                    );
                } else {
                    // Middle/back cards never show content
                    contentOpacity.value = withTiming(0, { duration: 150, easing: Easing.linear });
                }
            }
        }, [
            position,
            isExiting,
            onExitComplete,
            onForwardShiftComplete,
            translateX,
            translateY,
            scale,
            opacity,
            contentOpacity,
            width,
            cardPositions,
            cardHeight,
            lockedWrapperHeight,
            isHeightLocked,
            lockedCardHeight,
            exiting,
            exitingContentHeight,
        ]);

        // Animated height for the card container (syncs with measured content height).
        // Height source priority: exiting locked card > locked wrapper > cardHeight
        const animatedCardStyle = useAnimatedStyle(() => {
            const h =
                exiting.value === 1
                    ? lockedCardHeight.value
                    : isHeightLocked.value
                    ? lockedWrapperHeight.value
                    : cardHeight.value;
            return {
                transform: [{ scale: scale.value }],
                opacity: opacity.value,
                height: h,
            };
        });

        const translateStyle = useAnimatedStyle(() => ({
            transform: [{ translateX: translateX.value }, { translateY: translateY.value }],
        }));

        const contentStyle = useAnimatedStyle(() => ({
            opacity: contentOpacity.value,
        }));

        return (
            <Animated.View style={translateStyle}>
                <Animated.View style={[styles.card, animatedCardStyle]}>
                    {showShadow ? <CardShadow /> : null}

                    {/* Only the front card actually renders content */}
                    {position === 'front' ? (
                        <Animated.View style={[contentStyle, styles.contentWrapper]}>
                            {!data
                                ? renderSkeleton?.()
                                : renderContent(data, isExiting ? exitingContentHeight : cardHeight)}
                        </Animated.View>
                    ) : null}
                </Animated.View>
            </Animated.View>
        );
    }
);

export interface StackedCardsProps {
    // Current data to display on the front card
    data: RaceToPlaceEntry | null;
    // Render function for card content. Child MUST write its measured height to cardHeight.
    renderCard: (data: RaceToPlaceEntry, cardHeight: SharedValue<number>) => ReactNode;
    // Function to render skeleton when loading
    renderSkeleton?: () => ReactNode;
    // Optional styles for the outer container
    containerStyle?: ViewStyle;
    // Placeholder height used before content is measured
    placeholderHeight?: number;
}

export const StackedCards = ({
    data,
    renderCard,
    renderSkeleton,
    containerStyle,
    placeholderHeight = 100,
}: StackedCardsProps) => {
    const [cardStates, setCardStates] = useState<{
        front: { id: string; data: RaceToPlaceEntry | null; isExiting: boolean };
        middle: { id: string; data: null };
        back: { id: string; data: null };
    }>({
        front: { id: 'card-1', data: data, isExiting: false },
        middle: { id: 'card-2', data: null },
        back: { id: 'card-3', data: null },
    });

    // Height written by child content
    const cardHeight = useSharedValue(placeholderHeight);
    // Locked snapshot used during transitions to prevent jumps
    const lockedWrapperHeight = useSharedValue(placeholderHeight);
    // true = use locked height, false = use cardHeight
    const isHeightLocked = useSharedValue(false);

    // Monotonically increasing id for new back cards (keeps React keys unique)
    const nextCardIdRef = useRef(4);
    // Holds incoming data while the current front card is exiting
    const pendingDataRef = useRef<RaceToPlaceEntry | null>(null);
    // Prevents overlapping exit/enter animations
    const isAnimatingRef = useRef(false);

    // Lock height at current value to prevent jumps during card transitions
    const lockHeight = useCallback(() => {
        lockedWrapperHeight.value = cardHeight.value;
        isHeightLocked.value = true;
        cancelAnimation(cardHeight);
    }, [cardHeight, lockedWrapperHeight, isHeightLocked]);

    // Start exit animation for the current front card
    const triggerExitAnimation = useCallback(() => {
        if (isAnimatingRef.current) return;
        isAnimatingRef.current = true;
        lockHeight();
        setCardStates(prev => ({ ...prev, front: { ...prev.front, isExiting: true } }));
    }, [lockHeight]);

    // When raceToPlaceEntryId changes and we're free to animate,
    // queue the new data and trigger the front card exit.
    useEffect(() => {
        const currentData = cardStates.front.data;
        if (data && !isAnimatingRef.current) {
            // New entry - trigger animation
            if (data.raceToPlaceEntryId !== currentData?.raceToPlaceEntryId) {
                pendingDataRef.current = data;
                triggerExitAnimation();
            }
            // Same entry, different data - update silently
            else if (!isEqual(data, currentData)) {
                setCardStates(prev => ({ ...prev, front: { ...prev.front, data: data } }));
            }
        }
    }, [cardStates.front.data, data, triggerExitAnimation]);

    const completeTransition = useCallback(() => {
        // Mark that the full cycle is done (exit + resize)
        isAnimatingRef.current = false;
    }, []);

    // Rotate card instances by changing keys:
    // - previous middle -> front (with new data)
    // - previous back -> middle
    // - new id -> back
    const handleExitComplete = useCallback(() => {
        const newData = pendingDataRef.current;
        pendingDataRef.current = null;
        setCardStates(prev => ({
            front: { id: prev.middle.id, data: newData, isExiting: false },
            middle: { id: prev.back.id, data: null },
            back: { id: `card-${nextCardIdRef.current}`, data: null },
        }));
        nextCardIdRef.current += 1;
    }, []);

    // Animate locked height to new content height, then unlock
    const handleForwardShiftComplete = useCallback(() => {
        runOnUI(() => {
            'worklet';
            // Smooth height transition: locked value animates to match new content
            lockedWrapperHeight.value = withTiming(cardHeight.value, HEIGHT_ANIMATION_CONFIG, finished => {
                if (finished) {
                    isHeightLocked.value = false; // Unlock to resume height tracking
                    runOnJS(completeTransition)(); // Allow new transitions
                }
            });
        })();
    }, [lockedWrapperHeight, cardHeight, isHeightLocked, completeTransition]);

    // Wrapper height matches the card height (locked or current)
    const wrapperStyle = useAnimatedStyle(() => ({
        height: isHeightLocked.value ? lockedWrapperHeight.value : cardHeight.value,
    }));

    return (
        <Animated.View style={[styles.wrapper, containerStyle, wrapperStyle]}>
            {/* Back card: visual only, no content */}
            <Card
                key={cardStates.back.id}
                position="back"
                data={null}
                isExiting={false}
                renderContent={renderCard}
                showShadow
                cardHeight={cardHeight}
                lockedWrapperHeight={lockedWrapperHeight}
                isHeightLocked={isHeightLocked}
            />
            {/* Middle card: visual only, no content */}
            <Card
                key={cardStates.middle.id}
                position="middle"
                data={null}
                isExiting={false}
                renderContent={renderCard}
                showShadow={!cardStates.front.isExiting}
                cardHeight={cardHeight}
                lockedWrapperHeight={lockedWrapperHeight}
                isHeightLocked={isHeightLocked}
            />
            {/* Front card: only card that shows real content and can exit */}
            <Card
                key={cardStates.front.id}
                position="front"
                data={cardStates.front.data}
                isExiting={cardStates.front.isExiting}
                onExitComplete={handleExitComplete}
                onForwardShiftComplete={handleForwardShiftComplete}
                renderContent={renderCard}
                renderSkeleton={renderSkeleton}
                cardHeight={cardHeight}
                lockedWrapperHeight={lockedWrapperHeight}
                isHeightLocked={isHeightLocked}
            />
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    wrapper: {
        width: '100%',
        position: 'relative',
    },
    card: {
        // Use top as transform origin so scaling keeps the top edge aligned
        transformOrigin: 'top center',
        position: 'absolute',
        width: '100%',
        backgroundColor: ds.colorValue.background.neutral.subtler,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: ds.colorValue.border.subtle,
    },
    contentWrapper: {
        // Content anchored to bottom of card.
        // As card HEIGHT animates, content "grows upward" from the bottom edge.
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
    },
});
